import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import CodeViewer from "@/components/CodeViewer";
import { api, API, getToken } from "@/lib/api";
import { consumeSSE, stripFence } from "@/lib/sse";
import { toast } from "sonner";
import { Sparkles, Save, Cpu, Zap } from "lucide-react";

const EXAMPLES = [
  "A scalping EA for EURUSD M15 using two EMAs (fast 9, slow 21) — buy on golden cross, sell on death cross. 20 pip SL, 40 pip TP, trailing stop of 15 pips.",
  "Trend-follower on GBPUSD H1: RSI must be above 55 AND MACD line above signal line for buy. Exit when RSI crosses below 45. Risk 30 pips SL, 90 pips TP.",
  "Mean-reversion bot: BUY when price crosses above the lower Bollinger band (period 20, dev 2) and RSI < 30. SELL when price crosses below upper Bollinger and RSI > 70.",
];

export default function AIGenerator() {
  const nav = useNavigate();
  const [models, setModels] = useState([]);
  const [model, setModel] = useState("claude-sonnet-4-6");
  const [prompt, setPrompt] = useState("");
  const [name, setName] = useState("AI Strategy");
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const [streaming, setStreaming] = useState(true);
  const [platform, setPlatform] = useState("mt4");
  const abortRef = useRef(null);

  useEffect(() => {
    api.get("/models").then(({data}) => setModels(data.models)).catch(()=>{});
  }, []);

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Describe your strategy first"); return; }
    setBusy(true);
    setCode("");

    if (!streaming) {
      try {
        const { data } = await api.post("/generate/ai", { prompt: prompt.trim(), model, platform });
        setCode(data.mql4_code || "");
        toast.success(`Generated with ${model}`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Generation failed");
      } finally { setBusy(false); }
      return;
    }

    // Streaming mode via SSE fetch
    try {
      const token = getToken();
      const controller = new AbortController();
      abortRef.current = controller;
      const res = await fetch(`${API}/generate/ai/stream`, {
        method: "POST",
        credentials: "include",
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ prompt: prompt.trim(), model, platform }),
      });
      let rawAccum = "";
      await consumeSSE(res, (event, payload) => {
        try {
          const parsed = JSON.parse(payload);
          if (parsed.chunk) {
            rawAccum += parsed.chunk;
            setCode(stripFence(rawAccum));
          }
        } catch (err) { console.debug("[ai-stream] non-JSON SSE frame ignored", err); }
      });
      toast.success(`Generated with ${model}`);
    } catch (e) {
      if (e?.name === "AbortError" || /aborted/i.test(String(e?.message || ""))) {
        toast.info("Stopped");
      } else {
        toast.error(e.message || "Streaming failed");
      }
    } finally { setBusy(false); abortRef.current = null; }
  };

  const stop = () => {
    if (abortRef.current) { abortRef.current.abort(); toast("Generation stopped"); }
  };

  const save = async () => {
    if (!code) { toast.error("Generate first"); return; }
    try {
      const { data } = await api.post("/strategies", {
        name: name || "AI Strategy",
        source: "ai",
        config: { name, platform, ai_prompt: prompt, ai_model: model },
        mql4_code: code,
      });
      toast.success("Saved");
      nav(`/dashboard`);
    } catch { toast.error("Save failed"); }
  };

  const safe = (name || "AI_Strategy").replace(/[^A-Za-z0-9_]+/g, "_");
  const ext = platform === "mt5" ? "mq5" : "mq4";

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="mb-8">
          <div className="chip mb-3"><Cpu size={12}/> AI GENERATOR</div>
          <h1 className="font-display text-4xl text-white tracking-tight">Describe it. Ship it.</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1 max-w-2xl">
            Write your strategy in plain English. Pick a platform + model. Get a compile-ready <span className="font-mono text-white">.mq4</span> or <span className="font-mono text-white">.mq5</span>.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <section className="lg:col-span-5 space-y-5">
            <div className="card-sharp p-5">
              <label className="label-tiny">Strategy name</label>
              <input data-testid="ai-name-input" value={name} onChange={(e)=>setName(e.target.value)} className="input-dark" placeholder="My AI Strategy"/>

              <label className="label-tiny mt-5">Platform</label>
              <div className="flex gap-1 border border-white/10 rounded p-0.5 w-fit" data-testid="ai-platform-toggle">
                <button data-testid="ai-platform-mt4" onClick={()=>setPlatform("mt4")}
                  className={`px-4 py-1.5 text-xs font-mono rounded transition-colors ${platform==="mt4" ? "bg-[var(--accent)] text-black" : "text-[var(--text-secondary)] hover:text-white"}`}>MT4 · .mq4</button>
                <button data-testid="ai-platform-mt5" onClick={()=>setPlatform("mt5")}
                  className={`px-4 py-1.5 text-xs font-mono rounded transition-colors ${platform==="mt5" ? "bg-[var(--accent)] text-black" : "text-[var(--text-secondary)] hover:text-white"}`}>MT5 · .mq5</button>
              </div>

              <label className="label-tiny mt-5">Model</label>
              <div className="grid grid-cols-1 gap-2" data-testid="ai-model-list">
                {models.map((m) => (
                  <label key={m.id} className={`border rounded p-3 cursor-pointer flex items-start gap-3 transition-colors
                    ${model===m.id ? "border-[var(--accent)] bg-[var(--accent)]/5" : "border-white/10 hover:border-white/25"}`}
                    data-testid={`ai-model-${m.id}`}>
                    <input type="radio" checked={model===m.id} onChange={()=>setModel(m.id)}
                      className="mt-1 accent-[var(--accent)]"/>
                    <div>
                      <div className="font-display text-sm text-white">{m.label}</div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">{m.provider}</div>
                    </div>
                  </label>
                ))}
              </div>

              <label className="label-tiny mt-5">Describe your strategy</label>
              <textarea data-testid="ai-prompt-input" value={prompt} onChange={(e)=>setPrompt(e.target.value)}
                rows={8}
                className="input-dark !font-sans !font-normal !text-sm"
                placeholder="e.g. Trend-follower on GBPUSD H1 using RSI > 55 and MACD line above signal for buy…"/>
              <div className="mt-3 flex flex-wrap gap-1.5" data-testid="ai-examples">
                {EXAMPLES.map((ex, i) => (
                  <button key={ex} onClick={()=>setPrompt(ex)}
                    data-testid={`ai-example-${i}`}
                    className="chip hover:!border-[var(--accent)] hover:!text-[var(--accent)] cursor-pointer text-left">
                    Example {i+1}
                  </button>
                ))}
              </div>
              <div className="mt-5 flex gap-2">
                <label className="flex items-center gap-2 mr-auto text-xs font-mono text-[var(--text-secondary)] cursor-pointer" data-testid="ai-stream-toggle-label">
                  <input data-testid="ai-stream-toggle" type="checkbox" checked={streaming}
                    onChange={(e)=>setStreaming(e.target.checked)}
                    className="accent-[var(--accent)]"/>
                  <Zap size={12} className="text-[var(--accent)]"/> STREAM
                </label>
                {busy ? (
                  <button data-testid="ai-stop-btn" onClick={stop}
                    className="btn-outline flex items-center gap-2 hover:!border-[var(--loss)] hover:!text-[var(--loss)]">Stop</button>
                ) : null}
                <button data-testid="ai-generate-btn" onClick={generate} disabled={busy}
                  className="btn-primary flex items-center gap-2 flex-1 justify-center">
                  <Sparkles size={14}/> {busy ? (streaming ? "Streaming…" : "Generating…") : `Generate ${platform === "mt5" ? "MQL5" : "MQL4"}`}
                </button>
                <button data-testid="ai-save-btn" onClick={save} disabled={!code || busy}
                  className="btn-outline flex items-center gap-2">
                  <Save size={14}/> Save
                </button>
              </div>
            </div>
          </section>

          <section className="lg:col-span-7">
            <div className="card-sharp overflow-hidden min-h-[520px] flex flex-col">
              {busy && !code ? (
                <div className="flex items-center justify-center h-full py-24">
                  <div className="text-center">
                    <div className="h-12 w-12 rounded-full border-2 border-white/10 border-t-[var(--accent)] animate-spin mx-auto"></div>
                    <p className="text-xs font-mono uppercase tracking-widest text-[var(--text-secondary)] mt-4">
                      {streaming ? `${model} is streaming…` : `${model} is writing your robot…`}
                    </p>
                  </div>
                </div>
              ) : code ? (
                <CodeViewer code={code} filename={`${safe}.${ext}`}/>
              ) : (
                <div className="flex items-center justify-center h-full py-24" data-testid="ai-empty-state">
                  <div className="text-center max-w-md">
                    <div className="h-12 w-12 rounded-sm border border-white/10 mx-auto flex items-center justify-center">
                      <Sparkles size={20} className="text-[var(--accent)]"/>
                    </div>
                    <h3 className="font-display text-xl text-white mt-4">Nothing to see yet</h3>
                    <p className="text-sm text-[var(--text-secondary)] mt-2">
                      Describe your strategy and hit generate. Your MQL4 code will appear here.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
